import React, { useCallback, useEffect, useState } from 'react'
import {
    Checkbox,
    Dropdown,
    Form,
    Input,
    Label,
    Message,
    Pagination,
    Segment,
    Table,
} from 'semantic-ui-react'
import { AppPrimaryButton } from 'elements'
import { LoaderBar } from 'components'
import { VoiceService } from 'utility/services/voice'
import { useNotification } from 'hooks/toast'
import {
    getVoiceApiErrorMessage,
    normalizeVoiceCompanySetting,
    normalizeVoiceLanguages,
    normalizeVoiceTranslationsPage,
    reviewedFilterValue,
    totalPages,
    voiceLanguageOptions,
} from './voice-utils'
import { VoiceAccuracyReport } from './VoiceAccuracyReport'

const PAGE_SIZE = 25

/**
 * Settings > Voice inspection (company admins). The mobile app reads questions
 * aloud and takes spoken answers; here the admin turns it on or off, picks the
 * language notes are saved in and the default reading language, and reviews
 * the stored question translations.
 */
export const VoiceInspectionSettings = () => {
    const { successToast, errorToast } = useNotification()
    const [languages, setLanguages] = useState([])
    const [setting, setSetting] = useState(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [forbidden, setForbidden] = useState(false)
    const [error, setError] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const [langs, company] = await Promise.all([
                VoiceService.getLanguages(),
                VoiceService.getCompanySetting(),
            ])
            setLanguages(normalizeVoiceLanguages(langs))
            setSetting(normalizeVoiceCompanySetting(company))
        } catch (err) {
            if (err?.status === 403) setForbidden(err?.error?.ErrorCode === 'VoiceNotAvailable' ? 'not-available' : true)
            else setError(getVoiceApiErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const save = async () => {
        if (!setting) return
        setSaving(true)
        setError('')
        try {
            const res = await VoiceService.saveCompanySetting(setting)
            setSetting(normalizeVoiceCompanySetting(res))
            successToast('Voice inspection settings saved')
        } catch (err) {
            if (err?.status === 403) {
                setForbidden(true)
                errorToast('Only an admin can change these settings.')
            } else {
                const message = getVoiceApiErrorMessage(err)
                setError(message)
                errorToast(message)
            }
        } finally {
            setSaving(false)
        }
    }

    if (loading) return <LoaderBar content="Loading voice settings…" />
    if (forbidden) {
        return (
            <Message warning>
                {forbidden === 'not-available'
                    ? 'Voice inspection is not available for your company yet. Contact eAuditor support to have it turned on.'
                    : 'Only an admin can view or change voice inspection settings.'}
            </Message>
        )
    }

    return (
        <>
            <Segment raised>
                <Form>
                    <h3 style={{ marginTop: 0 }}>Voice inspection</h3>
                    <p style={{ color: '#666', maxWidth: 760 }}>
                        In the mobile app, inspectors tap <b>Voice</b> at the top of
                        an inspection: the phone reads each question aloud in their
                        language and they answer by voice (&ldquo;Yes&rdquo;,
                        &ldquo;No cumple&rdquo;, &ldquo;24&rdquo;, &ldquo;take
                        picture&rdquo;, &ldquo;note …&rdquo;). Answers are saved
                        exactly as if tapped, so reports and scores do not change.
                    </p>
                    {error && (
                        <Message negative onDismiss={() => setError('')}>
                            {error}
                        </Message>
                    )}
                    {setting && (
                        <>
                            <Form.Field>
                                <Checkbox
                                    toggle
                                    label="Allow voice inspection mode for this company"
                                    checked={setting.voiceEnabled}
                                    onChange={(_, d) =>
                                        setSetting({ ...setting, voiceEnabled: !!d.checked })
                                    }
                                />
                            </Form.Field>
                            <Form.Group widths="equal">
                                <Form.Field>
                                    <label>Spoken notes are saved in</label>
                                    <Dropdown
                                        selection
                                        search
                                        options={voiceLanguageOptions(languages)}
                                        value={setting.noteLanguage}
                                        onChange={(_, d) =>
                                            setSetting({ ...setting, noteLanguage: d.value })
                                        }
                                    />
                                    <small style={{ color: '#777' }}>
                                        What the inspector said is kept next to the
                                        translated note.
                                    </small>
                                </Form.Field>
                                <Form.Field>
                                    <label>Read questions in (default for everyone)</label>
                                    <Dropdown
                                        selection
                                        search
                                        options={voiceLanguageOptions(
                                            languages,
                                            "Template's own language",
                                        )}
                                        value={setting.defaultHearLanguage}
                                        onChange={(_, d) =>
                                            setSetting({
                                                ...setting,
                                                defaultHearLanguage: d.value,
                                            })
                                        }
                                    />
                                    <small style={{ color: '#777' }}>
                                        Each inspector can choose another language in
                                        the app (Settings &gt; Voice inspection).
                                    </small>
                                </Form.Field>
                            </Form.Group>
                            <AppPrimaryButton
                                type="button"
                                onClick={save}
                                loading={saving}
                                disabled={saving}
                            >
                                Save
                            </AppPrimaryButton>
                        </>
                    )}
                </Form>
            </Segment>
            <VoiceAccuracyReport />
            <VoiceTranslationReview languages={languages} />
        </>
    )
}

const VoiceTranslationReview = ({ languages }) => {
    const { successToast, errorToast } = useNotification()
    const [lang, setLang] = useState('')
    const [reviewed, setReviewed] = useState('unreviewed')
    const [search, setSearch] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [page, setPage] = useState(1)
    const [data, setData] = useState({ totalCount: 0, items: [], page: 1, size: PAGE_SIZE })
    const [drafts, setDrafts] = useState({})
    const [savingId, setSavingId] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    const load = useCallback(async () => {
        setLoading(true)
        setError('')
        try {
            const res = await VoiceService.listTranslations({
                lang,
                reviewed: reviewedFilterValue(reviewed),
                search: appliedSearch,
                page,
                size: PAGE_SIZE,
            })
            setData(normalizeVoiceTranslationsPage(res))
            setDrafts({})
        } catch (err) {
            setError(getVoiceApiErrorMessage(err))
        } finally {
            setLoading(false)
        }
    }, [lang, reviewed, appliedSearch, page])

    useEffect(() => {
        load()
    }, [load])

    const saveRow = async row => {
        const text = (drafts[row.id] ?? row.translatedText).trim()
        if (!text) {
            errorToast('The translation cannot be empty.')
            return
        }
        setSavingId(row.id)
        try {
            await VoiceService.updateTranslation(row.id, text)
            successToast('Translation saved and marked as reviewed')
            setData(prev => ({
                ...prev,
                items: prev.items.map(i =>
                    i.id === row.id ? { ...i, translatedText: text, isReviewed: true } : i,
                ),
            }))
            setDrafts(prev => {
                const next = { ...prev }
                delete next[row.id]
                return next
            })
        } catch (err) {
            errorToast(getVoiceApiErrorMessage(err))
        } finally {
            setSavingId(null)
        }
    }

    return (
        <Segment raised>
            <h3 style={{ marginTop: 0 }}>Question translations</h3>
            <p style={{ color: '#666', maxWidth: 760 }}>
                Questions and answer options are translated automatically the first
                time an inspector hears them in another language. Correct a wording
                here and save it: the new text is used from then on and marked as
                reviewed.
            </p>
            <Form>
                <Form.Group widths="equal">
                    <Form.Field>
                        <label>Language</label>
                        <Dropdown
                            selection
                            options={voiceLanguageOptions(languages, 'All languages')}
                            value={lang}
                            onChange={(_, d) => {
                                setLang(d.value)
                                setPage(1)
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Show</label>
                        <Dropdown
                            selection
                            options={[
                                { key: 'unreviewed', value: 'unreviewed', text: 'Not reviewed yet' },
                                { key: 'reviewed', value: 'reviewed', text: 'Reviewed' },
                                { key: 'all', value: 'all', text: 'All' },
                            ]}
                            value={reviewed}
                            onChange={(_, d) => {
                                setReviewed(d.value)
                                setPage(1)
                            }}
                        />
                    </Form.Field>
                    <Form.Field>
                        <label>Search</label>
                        <Input
                            icon="search"
                            placeholder="Original or translated text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    setAppliedSearch(search.trim())
                                    setPage(1)
                                }
                            }}
                            onBlur={() => {
                                if (search.trim() !== appliedSearch) {
                                    setAppliedSearch(search.trim())
                                    setPage(1)
                                }
                            }}
                        />
                    </Form.Field>
                </Form.Group>
            </Form>
            {error && <Message negative>{error}</Message>}
            {loading && <LoaderBar content="Loading translations…" />}
            {!loading && data.items.length === 0 && (
                <Message info>No translations match these filters.</Message>
            )}
            {!loading && data.items.length > 0 && (
                <>
                    <Table celled compact stackable>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell width={1}>Language</Table.HeaderCell>
                                <Table.HeaderCell width={6}>Original</Table.HeaderCell>
                                <Table.HeaderCell width={7}>Translation</Table.HeaderCell>
                                <Table.HeaderCell width={2} />
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {data.items.map(row => {
                                const draft = drafts[row.id]
                                const changed = draft !== undefined && draft.trim() !== row.translatedText
                                return (
                                    <Table.Row key={row.id}>
                                        <Table.Cell>{row.targetLanguage}</Table.Cell>
                                        <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>
                                            {row.sourceText}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Form>
                                                <Form.TextArea
                                                    rows={2}
                                                    value={draft ?? row.translatedText}
                                                    onChange={e =>
                                                        setDrafts(prev => ({ ...prev, [row.id]: e.target.value }))
                                                    }
                                                />
                                            </Form>
                                            {row.isReviewed ? (
                                                <Label size="mini" color="green">Reviewed</Label>
                                            ) : (
                                                <Label size="mini">Automatic</Label>
                                            )}
                                        </Table.Cell>
                                        <Table.Cell textAlign="center">
                                            <AppPrimaryButton
                                                type="button"
                                                size="small"
                                                onClick={() => saveRow(row)}
                                                loading={savingId === row.id}
                                                disabled={savingId !== null || (!changed && row.isReviewed)}
                                            >
                                                {changed || !row.isReviewed ? 'Save' : 'Saved'}
                                            </AppPrimaryButton>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            })}
                        </Table.Body>
                    </Table>
                    {totalPages(data.totalCount, PAGE_SIZE) > 1 && (
                        <Pagination
                            activePage={page}
                            totalPages={totalPages(data.totalCount, PAGE_SIZE)}
                            onPageChange={(_, d) => setPage(Number(d.activePage) || 1)}
                        />
                    )}
                </>
            )}
        </Segment>
    )
}
